import React from 'react';
import {ResourceType} from '../../../@types/resource-type';
import GroupEditorController from './editors/GroupEditorController';
import SafetyModelEditor from './editors/SafetyModelEditor';
import EventSequenceSummaryEditor from './editors/EventSequenceSummaryEditor';
import EsdHistoryEditor from './editors/EsdHistoryEditor';
import EventSequenceTreeEditor from './editors/EventSequenceTreeEditor';
import EventSequenceTreeViewer from './editors/EventSequenceTreeViewer';
import SensitivityAnalysisEditor from './editors/SensitivityAnalysisEditor';
import UncertaintyAnalysisEditor from './editors/UncertaintyAnalysisEditor';
import UserEditor from './editors/UserEditor';
import ServiceUserAccountEditor from './editors/ServiceUserAccountEditor';
import OccurrenceReportDescriptorEditor
    from './editors/occurrence-report-descriptor-editor/OccurrenceReportDescriptorEditor';
import OccurrenceReportEditor from './editors/OccurrenceReportEditor';
import NewOccurrenceReportEditorController from './editors/NewOccurrenceReportEditorController';
import PsfTemplateWithLinksEditor from './editors/PsfTemplateWithLinksEditor';
import GroupRolePermissionEditor from './editors/GroupRolePermissionEditor';
import PlaceholderEditor from './editors/PlaceholderEditor';
import IntegratedRiskPictureEditor from './editors/IntegratedRiskPictureEditor';
import IRPWorkbook from './editors/irp-workbook';
import ComponentEditor from './editors/ComponentEditor';
import SafetyRecommendationSearchEditor from './editors/safety-recommendations/SafetyRecommendationSearchEditor';
import SafetyRecommendationEditor from './editors/safety-recommendations/SafetyRecommendationEditor';
import ComponentTreeEditor from './editors/ComponentTreeEditor';
import ComponentMappingEditor from './editors/component-mapping/ComponentMappingEditor';
import ComponentHistoryEditor from './editors/ComponentHistoryEditor';
import ComponentHistoryTreeViewer from './editors/ComponentHistoryTreeViewer';
import {makeGroup} from '../../../utils/group-utils';
import {makeSafetyModel} from '../../../utils/safety-model-utils';
import {makeComponent} from '../../../utils/component-utils';
import {newCamelCaseToWordWithNoS} from '../../../utils/utils';

/**
 * Configuration for editor components
 */
export interface EditorConfig {
    component: React.ComponentType<any>;
    prepareObject?: (object: any) => any;
    propName?: string;
}
/**
 * Map of resource types to their editor configurations
 */
export const EDITOR_CONFIG: Record<ResourceType, EditorConfig> = {
    [ResourceType.GROUPS]: {
        component: GroupEditorController,
        prepareObject: (obj) => makeGroup(obj),
        propName: 'group'
    },
    [ResourceType.SAFETY_MODELS]: {
        component: SafetyModelEditor,
        prepareObject: (obj) => makeSafetyModel(obj),
        propName: 'safetyModel'
    },
    [ResourceType.EVENT_SEQUENCES]: {
        component: EventSequenceSummaryEditor,
        propName: 'eventSequenceSummary'
    },
    [ResourceType.EVENT_SEQUENCE_HISTORY]: {
        component: EsdHistoryEditor,
        propName: 'eventSequenceSummary'
    },
    [ResourceType.EVENT_SEQUENCE_TREES]: {
        component: EventSequenceTreeEditor,
        propName: 'eventSequenceSummary'
    },
    [ResourceType.EVENT_SEQUENCE_HISTORY_TREES]: {
        component: EventSequenceTreeViewer,
        propName: 'esv'
    },
    [ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS]: {
        component: SensitivityAnalysisEditor,
        propName: 'eventSequenceSummary'
    },
    [ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS]: {
        component: UncertaintyAnalysisEditor,
        propName: 'eventSequenceSummary'
    },
    [ResourceType.USERS]: {
        component: UserEditor,
        propName: 'user'
    },
    [ResourceType.SERVICE_USER_ACCOUNTS]: {
        component: ServiceUserAccountEditor,
        propName: 'serviceUserAccount'
    },
    [ResourceType.OCCURRENCE_REPORT_DESCRIPTORS]: {
        component: OccurrenceReportDescriptorEditor,
        propName: 'occurrenceReportDescriptor'
    },
    [ResourceType.OCCURRENCE_REPORTS]: {
        component: OccurrenceReportEditor,
        propName: 'occurrence'
    },
    [ResourceType.NEW_OCCURRENCE_REPORTS]: {
        component: NewOccurrenceReportEditorController,
        propName: 'occurrence'
    },
    [ResourceType.FOUNDRY_EVENTS]: {
        component: NewOccurrenceReportEditorController,
        propName: 'occurrence'
    },
    [ResourceType.PERFORMANCE_SHAPING_FACTORS]: {
        component: PsfTemplateWithLinksEditor,
        propName: 'psfTemplate'
    },
    [ResourceType.GROUP_ROLE_PERMISSIONS]: {
        component: GroupRolePermissionEditor
    },
    // Placeholders for other resource types - update with correct components when available
    [ResourceType.OCCURRENCE_TYPES]: {
        component: GroupRolePermissionEditor
    },
    [ResourceType.TRAFFIC_MODELS]: {
        component: PlaceholderEditor
    },
    [ResourceType.OPERATIONAL_CHANGES]: {
        component: PlaceholderEditor
    },
    [ResourceType.RISK_FORECASTS]: {
        component: PlaceholderEditor
    },
    [ResourceType.WORKSHOPS]: {
        component: PlaceholderEditor
    },
    [ResourceType.INTEGRATED_RISK_PICTURES]: {
        component: IntegratedRiskPictureEditor,
        propName: 'integratedRiskPicture'
    },
    [ResourceType.IRP_WORKBOOK]: {
        component: IRPWorkbook,
        propName: 'integratedRiskPicture'
    },
    [ResourceType.SAFETY_RECOMMENDATIONS]: {
        component: SafetyRecommendationSearchEditor,
        propName: 'register'
    },
    [ResourceType.SAFETY_RECOMMENDATION]: {
        component: SafetyRecommendationEditor,
        propName: 'recommendation'
    },
    [ResourceType.COMPONENT]: {
        component: ComponentEditor,
        prepareObject: (obj) => makeComponent(obj),
        propName: 'component'
    },
    [ResourceType.COMPONENT_TREE]: {
        component: ComponentTreeEditor,
        prepareObject: (obj) => makeComponent(obj),
        propName: 'component'
    },
    [ResourceType.COMPONENT_MAPPING]: {
        component: ComponentMappingEditor,
        prepareObject: (obj) => makeComponent(obj),
        propName: 'component'
    },
    [ResourceType.COMPONENT_HISTORY]: {
        component: ComponentHistoryEditor,
        prepareObject: (obj) => makeComponent(obj),
        propName: 'component'
    },
    // Keyed by the history entry's id, so each version opened gets its own read-only tab.
    [ResourceType.COMPONENT_HISTORY_TREE]: {
        component: ComponentHistoryTreeViewer,
        propName: 'componentVersion'
    },
    // The catalogue is never itself opened as a tab: a row click rewrites COMPONENT to
    // COMPONENT_TREE before a tab ever opens (component-solution-spec.md section 5). This entry
    // exists only so the exhaustive Record<ResourceType, ...> type below is satisfied.
    [ResourceType.COMPONENT_CATALOGUE]: {
        component: ComponentTreeEditor,
        prepareObject: (obj) => makeComponent(obj),
        propName: 'component'
    },
    [ResourceType.UNKNOWN]: {
        component: GroupRolePermissionEditor
    }
};

/**
 * Configuration for tab label generation
 */
export interface TabLabelConfig {
    useObjectName?: boolean;
    propertyPath?: string;
    customLabelResolver?: (object: any) => string;
    defaultToTypeName?: boolean;
}

/**
 * Map of resource types to their tab label configurations
 */
export const TAB_LABEL_CONFIG: Record<ResourceType, TabLabelConfig> = {
    [ResourceType.GROUPS]: {
        useObjectName: true
    },
    [ResourceType.SAFETY_MODELS]: {
        useObjectName: true
    },
    [ResourceType.EVENT_SEQUENCES]: {
        useObjectName: true
    },
    [ResourceType.EVENT_SEQUENCE_HISTORY]: {
        useObjectName: true
    },
    [ResourceType.EVENT_SEQUENCE_TREES]: {
        useObjectName: true
    },
    [ResourceType.EVENT_SEQUENCE_HISTORY_TREES]: {
        customLabelResolver: (obj) => obj.eventSequenceDTO?.name || 'Safety View History Tree'
    },
    [ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS]: {
        useObjectName: true
    },
    [ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS]: {
        useObjectName: true
    },
    [ResourceType.USERS]: {
        useObjectName: true
    },
    [ResourceType.SERVICE_USER_ACCOUNTS]: {
        useObjectName: true
    },
    [ResourceType.OCCURRENCE_REPORT_DESCRIPTORS]: {
        // useObjectName: true
        customLabelResolver: (obj) => {
            return obj.name + (obj.version ? ` (v${obj.version})` : ' (DRAFT)');
        }
    },
    [ResourceType.OCCURRENCE_REPORTS]: {
        propertyPath: 'displayName'
    },
    [ResourceType.NEW_OCCURRENCE_REPORTS]: {
        propertyPath: 'uniqueId'
    },
    [ResourceType.FOUNDRY_EVENTS]: {
        propertyPath: 'uniqueId'
    },
    [ResourceType.PERFORMANCE_SHAPING_FACTORS]: {
        useObjectName: true
    },
    [ResourceType.GROUP_ROLE_PERMISSIONS]: {
        defaultToTypeName: true
    },
    [ResourceType.OCCURRENCE_TYPES]: {
        useObjectName: true
    },
    [ResourceType.TRAFFIC_MODELS]: {
        useObjectName: true
    },
    [ResourceType.OPERATIONAL_CHANGES]: {
        useObjectName: true
    },
    [ResourceType.RISK_FORECASTS]: {
        useObjectName: true
    },
    [ResourceType.WORKSHOPS]: {
        useObjectName: true
    },
    [ResourceType.INTEGRATED_RISK_PICTURES]: {
        useObjectName: true
    },
    [ResourceType.IRP_WORKBOOK]: {
        useObjectName: true
    },
    [ResourceType.SAFETY_RECOMMENDATIONS]: {
        customLabelResolver: (): string => 'Safety Recommendations'
    },
    [ResourceType.SAFETY_RECOMMENDATION]: {
        propertyPath: 'number'
    },
    [ResourceType.COMPONENT]: {
        useObjectName: true
    },
    [ResourceType.COMPONENT_TREE]: {
        useObjectName: true
    },
    [ResourceType.COMPONENT_MAPPING]: {
        customLabelResolver: (obj) => `${obj?.name ?? 'Component'} mapping`
    },
    [ResourceType.COMPONENT_HISTORY]: {
        customLabelResolver: (obj) => `${obj?.name ?? 'Component'} history`
    },
    [ResourceType.COMPONENT_HISTORY_TREE]: {
        customLabelResolver: (obj) => `${obj?.component?.name ?? 'Component'} #${obj?.revision ?? ''}`
    },
    [ResourceType.COMPONENT_CATALOGUE]: {
        useObjectName: true
    },
    [ResourceType.UNKNOWN]: {
        defaultToTypeName: true
    }
};

/**
 * Get tab label for an object based on its resource type
 */
export function getTabLabel(objectType: ResourceType, object: any): string {
    const config = TAB_LABEL_CONFIG[objectType];
    
    if (!config) {
        return newCamelCaseToWordWithNoS(objectType);
    }
    
    // Check custom resolver first
    if (config.customLabelResolver) {
        return config.customLabelResolver(object);
    }
    
    // Check property path
    if (config.propertyPath) {
        const value = getNestedProperty(object, config.propertyPath);
        if (value) return value;
    }
    
    // Check object name
    if (config.useObjectName && object.name && object.name !== '') {
        return object.name;
    }
    
    // Default to type name
    if (config.defaultToTypeName) {
        return newCamelCaseToWordWithNoS(objectType);
    }
    
    // Final fallback
    return newCamelCaseToWordWithNoS(objectType);
}

/**
 * Helper to get nested property value
 */
function getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
}

/**
 * Get editor configuration for a resource type
 */
export function getEditorConfig(resourceType: ResourceType): EditorConfig | undefined {
    return EDITOR_CONFIG[resourceType];
}